import { env } from '@/config/env'
import { getAccessToken, refreshAccessToken } from '@/services/session'

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  let response = await send(path, init)

  if (response.status === 401 && getAccessToken()) {
    await refreshAccessToken()
    response = await send(path, init)
  }

  if (!response.ok) {
    const error = (await response.json().catch(() => null)) as
      | { error?: string; message?: string; code?: string }
      | null
    throw new ApiError(
      error?.message ?? error?.error ?? 'Something went wrong',
      response.status,
      error?.code,
    )
  }

  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

/** For endpoints that return raw bytes (a resume file), not JSON — `apiRequest` always calls
 * `.json()`, which throws on binary. Same auth/refresh handling, different final parse. */
export async function apiRequestBlob(path: string, init?: RequestInit): Promise<Blob> {
  let response = await send(path, init)
  if (response.status === 401 && getAccessToken()) {
    await refreshAccessToken()
    response = await send(path, init)
  }
  if (!response.ok) throw new ApiError('Could not load file', response.status)
  return response.blob()
}

function send(path: string, init?: RequestInit) {
  const accessToken = getAccessToken()
  // FormData bodies (file uploads) need the browser to set Content-Type itself — it has to
  // include the multipart boundary, which nothing on this side of `fetch` can compute.
  const isFormData = init?.body instanceof FormData
  return fetch(`${env.talentBffUrl}${path}`, {
    ...init,
    // Every one of these responses is one talent's own data — never let the browser reuse a
    // cached copy after a logout+login as someone else in the same tab (see talent-bff's own
    // blanket `Cache-Control: no-store`, which this backs up rather than replaces: that header
    // stops OTHER clients/proxies from caching too, this stops the risk even if a future
    // response ever reaches here without it).
    cache: 'no-store',
    headers: {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...init?.headers,
    },
  })
}
